import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Dropdown,
  Form,
  Grid,
  Input,
  InputNumber,
  message,
  Modal,
  Popover,
  Progress,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tooltip,
} from "antd";
import {
  ArrowRightOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import * as dayjs from "dayjs";
import { useFirebase, useFirestore } from "react-redux-firebase";
import { FIRESTORE_PRODUCTS_TABLE, FIRESTORE_PURCHASE_ORDERS_TABLE } from "../../../../constants/FirestoreConstant";
import {
  PURCHASE_ORDER_STATUS_CANCELLED,
  PURCHASE_ORDER_STATUS_COMPLETED,
  PURCHASE_ORDER_STATUS_DRAFT,
  PURCHASE_ORDER_STATUS_IN_PRODUCTION,
  PURCHASE_ORDER_STATUS_PARTIALLY_SHIPPED,
  PURCHASE_ORDER_STATUS_PENDING_APPROVAL,
  PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
  PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW,
  PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION,
  PURCHASE_ORDER_STATUS_RECEIVED,
  PURCHASE_ORDER_STATUS_SHIPPED,
  PURCHASE_ORDER_STATUS_SPLIT,
  PURCHASE_ORDER_STATUS_UNDER_REVIEW,
} from "../../../../constants/OrderConstant";
import { addLogEntry } from "../../../../utils/logging";
import { useTranslation } from "react-i18next";
import urls from "../../../../urls";
import styled from "styled-components";
import {
  formatPrice,
  formatPriceByCurrency,
  getCurrencyPrefix,
  getPurchaseOrderStatusColor,
} from "../../../../functions/formatters";
import { usePurchaseOrder, usePurchaseOrderShipments, useSuppliers } from "../../../../data/hooks";
import { getFirebaseIDToken } from "../../../../functions/firebase/firebase";
import { usePurchaseOrderInvoices } from "../../../../data/hooks/usePurchaseOrderInvoices";
import { getShippedQuantityForOrder } from "../../../../utils/stockCalculations";
import { buildOpenItems, getOpenQuantity, summarizeOpenItems } from "../../../../functions/purchaseOrderOpenItems";
import { getPurchaseOrderOpenItemsExcelExportData } from "../../../../functions/excel";
import { exportToExcel } from "../../../../components/shared-components/ExcelExport";
import DiscontinuedMark from "../../../../components/shared-components/DiscontinuedMark";
import {
  DetailLayout,
  KeyValueList,
  Num,
  PageHeader,
  RecordLink,
  SectionCard,
  StickyActionBar,
} from "../../../../components/shared-components/purchase";

const { Panel } = Collapse;
const { TextArea } = Input;

// Wat er aan een orderregel verandert, voor de wijzigingen-popover in de
// actiebalk. `getPendingChanges` levert het type; dit maakt er leesbare tekst van.
const CHANGE_TYPE_LABELS = {
  added: "Regel toegevoegd",
  removed: "Regel verwijderd",
  amount: "Besteld aantal",
  cancelled: "Geannuleerd aantal",
  price: "Prijs per stuk",
};

// Zelfde labels/kleuren als op de zendingoverzichten, zodat een status overal
// hetzelfde heet.
const shipmentStatusColors = {
  draft: "default",
  shipped: "blue",
  in_transit: "orange",
  received: "green",
  completed: "cyan",
  cancelled: "red",
};

const shipmentStatusLabels = {
  draft: "Concept",
  shipped: "Verzonden",
  in_transit: "Onderweg",
  received: "Ontvangen",
  completed: "Afgerond",
  cancelled: "Geannuleerd",
};

const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const firestore = useFirestore();
  const firebase = useFirebase();
  const { t } = useTranslation();
  const [reviewForm] = Form.useForm();

  const [saving, setSaving] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProducts, setEditedProducts] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderNote, setReminderNote] = useState("");
  const [showOpenItemsModal, setShowOpenItemsModal] = useState(false);
  const [sendingOpenItems, setSendingOpenItems] = useState(false);
  const [openItemsNote, setOpenItemsNote] = useState("");

  const profile = useSelector((state) => state.firebaseReducer.profile);
  const { email } = profile;
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const { suppliers } = useSuppliers();
  const { shipments } = usePurchaseOrderShipments();
  const { order, loading, orderHistory, historyLoading, productDrafts } = usePurchaseOrder(id);

  const { invoices: allInvoices } = usePurchaseOrderInvoices({ realtime: false });

  const proformaInvoice = allInvoices.find((inv) => inv.type === "proforma" && inv.purchaseOrderId === id) || null;

  // Uitlopende producten (BMX-49). De vlag staat op het PRODUCT, niet op de orderregel: een
  // product kan op uitlopend gezet zijn nadat deze order al was aangemaakt, en dan hoort de
  // markering hier alsnog te verschijnen.
  const discontinuedProductIds = React.useMemo(
    () => new Set((products || []).filter((p) => p?.discontinued).map((p) => p.id)),
    [products],
  );
  const isProductDiscontinued = (productId) => discontinuedProductIds.has(productId);

  // Calculate shipped quantity for a product in this order
  const getShippedQuantity = (productId) => {
    if (!order) return 0;
    return getShippedQuantityForOrder(shipments, order.id, productId);
  };

  // Remaining OPEN quantity for a line: ordered minus what's already shipped minus what's been
  // cancelled. Never goes below 0, and never below what's already shipped (shipped/received
  // quantities stay intact).
  //
  // Delegeert sinds BMX-12 naar `getOpenQuantity`, zodat deze kolom, de Excel-export en de mail
  // naar de leverancier gegarandeerd hetzelfde getal laten zien. De berekening zelf is daarbij
  // ONGEWIJZIGD overgenomen, inclusief de `||` bij het bestelde aantal -- zie de uitgebreide
  // toelichting bij `getOrderedQuantity` in functions/purchaseOrderOpenItems.js. Dit is een
  // getal waar dubbele inkoop aan hangt; het hoort niet als bijvangst van een export-story te
  // veranderen.
  const getRemainingQuantity = (item) => getOpenQuantity(item, shipments, order?.id);

  // Diff editedProducts against the saved order.products — drives the pending-changes summary bar.
  // Purely local/derived; nothing here touches Firestore.
  const getPendingChanges = () => {
    if (!isEditing || !order) return [];
    const changes = [];

    editedProducts.forEach((item) => {
      const original = order.products.find((p) => p.productId === item.productId);
      const label = `${item.productName}${item.productColor ? ` (${item.productColor})` : ""}`;

      if (!original) {
        changes.push({ key: `${item.productId}-added`, label, type: "added", detail: `+${item.amount}x` });
        return;
      }

      if ((item.amount || 0) !== (original.amount || 0)) {
        changes.push({
          key: `${item.productId}-amount`,
          label,
          type: "amount",
          detail: `${original.amount} → ${item.amount}`,
        });
      }

      const oldCancelled = original.cancelledAmount || 0;
      const newCancelled = item.cancelledAmount || 0;
      if (newCancelled !== oldCancelled) {
        changes.push({
          key: `${item.productId}-cancelled`,
          label,
          type: "cancelled",
          detail: t("cancelled_amount_tag", { amount: newCancelled }),
        });
      }

      if ((item.price || 0) !== (original.price || 0)) {
        changes.push({
          key: `${item.productId}-price`,
          label,
          type: "price",
          detail: `${formatPriceByCurrency(original.price || 0, order.currency)} → ${formatPriceByCurrency(item.price || 0, order.currency)}`,
        });
      }
    });

    order.products.forEach((original) => {
      const stillExists = editedProducts.some((p) => p.productId === original.productId);
      if (!stillExists) {
        changes.push({
          key: `${original.productId}-removed`,
          label: `${original.productName}${original.productColor ? ` (${original.productColor})` : ""}`,
          type: "removed",
          detail: `-${original.amount}x`,
        });
      }
    });

    return changes;
  };

  useEffect(() => {
    if (order === null && !loading) {
      message.error(t("notifications.purchase_order_not_found"));
      navigate(urls.purchaseOrdersList);
    }
  }, [order, loading]);

  const loadOrder = async () => {};

  const handleOpenReviewModal = () => {
    // Initialize form with products data
    const initialValues = {
      products: order.products.map((product) => ({
        productId: product.productId,
        productName: product.productName,
        productColor: product.productColor,
        productCode: product.productCode,
        expectedAmount: product.adjustedAmount || product.amount,
        receivedAmount: product.adjustedAmount || product.amount,
        discrepancyReason: "",
      })),
    };
    reviewForm.setFieldsValue(initialValues);
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    try {
      const values = await reviewForm.validateFields();
      setSaving(true);

      const receivedProducts = values.products.map((product) => ({
        productId: product.productId,
        productCode: product.productCode,
        receivedAmount: product.receivedAmount,
        ...(product.receivedAmount !== product.expectedAmount && product.discrepancyReason
          ? { discrepancyReason: product.discrepancyReason }
          : {}),
      }));

      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "items_received",
        user: email,
        metadata: {
          totalReceived: receivedProducts.reduce((sum, p) => sum + p.receivedAmount, 0),
          discrepancies: receivedProducts.filter((p) => p.discrepancyReason).length,
        },
      });

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
        .doc(id)
        .update({
          receivedProducts,
          log: newLog,
          lastModifiedBy: { email, updatedAt: dayjs().unix() },
        });

      message.success(t("notifications.review_submitted_successfully"));
      setShowReviewModal(false);
      await loadOrder();
    } catch (error) {
      console.error("Error submitting review:", error);
      message.error(t("notifications.review_submission_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditedProducts(JSON.parse(JSON.stringify(order.products))); // Deep copy
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedProducts([]);
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...editedProducts];
    const product = { ...updated[index], [field]: value };

    // Already-shipped/received quantities must stay intact: never let the ordered amount
    // drop below what's already gone out in a shipment for this line.
    if (field === "amount") {
      const shippedQty = getShippedQuantity(product.productId);
      if (value < shippedQty) {
        message.error(t("cannot_reduce_below_shipped", { amount: shippedQty }));
        return;
      }
    }

    // If Bombyxx is changing the price, also update adjustedPrice to match
    // This ensures the change is properly saved and not overridden by supplier's previous adjustment
    if (field === "price") {
      product.adjustedPrice = value;
    }

    // If Bombyxx is changing the amount, also update adjustedAmount to match
    if (field === "amount") {
      product.adjustedAmount = value;
    }

    updated[index] = product;
    setEditedProducts(updated);
  };

  const handleRemoveProduct = (index) => {
    const product = editedProducts[index];
    const shippedQty = getShippedQuantity(product.productId);
    if (shippedQty > 0) {
      message.error(t("cannot_remove_shipped_product"));
      return;
    }

    Modal.confirm({
      title: t("remove_product"),
      content: t("remove_product_confirm"),
      okText: t("yes_remove"),
      cancelText: t("cancel"),
      okType: "danger",
      onOk: () => {
        const updated = editedProducts.filter((_, i) => i !== index);
        setEditedProducts(updated);
      },
    });
  };

  // Cancel the (remaining, not-yet-shipped) open quantity on a line. Shipped/received
  // amounts are untouched — this only ever reduces what's still open.
  const handleCancelRemaining = (index, cancelAmount) => {
    const product = editedProducts[index];
    const remaining = getRemainingQuantity(product);

    if (cancelAmount <= 0 || cancelAmount > remaining) return;

    Modal.confirm({
      title: t("cancel_remaining_quantity"),
      content: t("cancel_remaining_quantity_confirm", { amount: cancelAmount }),
      okText: t("yes_cancel"),
      cancelText: t("cancel"),
      okType: "danger",
      onOk: () => {
        const updated = [...editedProducts];
        updated[index] = {
          ...product,
          cancelledAmount: (product.cancelledAmount || 0) + cancelAmount,
        };
        setEditedProducts(updated);
      },
    });
  };

  const handleAddProduct = (productId) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;

    const newProduct = {
      productId: product.id,
      productName: product.name,
      productCode: product.code || "",
      productBarcode: product.barcode || "",
      productColor: product.color || "",
      productDimensions: product.dimensions || null,
      productImages: product.image || [],
      innerPackagingQuantity: product.innerPackagingQuantity || 0,
      masterPackagingQuantity: product.masterPackagingQuantity || 0,
      amount: 0,
      price: product.purchasePrice || 0,
      priority: false,
    };

    setEditedProducts([...editedProducts, newProduct]);
    message.success(`${product.name} ${t("added")}`);
  };

  const handleSaveAndSendToSupplier = async () => {
    try {
      setSaving(true);

      // Recalculate totalProducts and totalPrice based on edited products.
      // Cancelled quantities are excluded: the total should reflect what's actually still being fulfilled.
      const totalProducts = editedProducts.reduce((sum, product) => {
        const amount = product.adjustedAmount !== undefined ? product.adjustedAmount : product.amount;
        return sum + amount - (product.cancelledAmount || 0);
      }, 0);

      const totalPrice = editedProducts.reduce((sum, product) => {
        const price = product.adjustedPrice !== undefined ? product.adjustedPrice : product.price || 0;
        const amount = product.adjustedAmount !== undefined ? product.adjustedAmount : product.amount;
        return sum + price * (amount - (product.cancelledAmount || 0));
      }, 0);

      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "bombyxx_modifications",
        user: email,
        metadata: {
          message: "Bombyxx made modifications",
          totalProducts,
          totalPrice,
          previousStatus: order.status,
        },
      });

      const updateData = {
        products: editedProducts,
        totalProducts,
        totalPrice,
        log: newLog,
        lastModifiedBy: { email, updatedAt: dayjs().unix() },
      };

      // Only change status to pending_supplier_review if the order is in a review-related status
      // Otherwise keep the current status (Bombyxx is just making corrections)
      if (
        order.status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL ||
        order.status === PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION
      ) {
        updateData.status = PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW;
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update(updateData);

      // Sync product prices and packaging to products collection
      await updateProductPricesAndPackaging(editedProducts);

      message.success(t("changes_saved_sent_supplier"));
      setIsEditing(false);
      await loadOrder();
    } catch (error) {
      console.error("Error saving changes:", error);
      message.error(t("error_saving_changes"));
    } finally {
      setSaving(false);
    }
  };

  const updateProductPricesAndPackaging = async (orderProducts) => {
    const updatePromises = orderProducts.map(async (orderProduct) => {
      // Only update products that have a productId (not draft products)
      if (!orderProduct.productId) return;

      const product = products.find((p) => p.id === orderProduct.productId);
      if (!product) return;

      const updates = {};
      let hasUpdates = false;

      // Check if purchase price differs
      if (orderProduct.price !== undefined && orderProduct.price !== product.purchasePrice) {
        updates.purchasePrice = orderProduct.price;
        hasUpdates = true;
      }

      // Check if inner packaging quantity differs
      if (
        orderProduct.innerPackagingQuantity !== undefined &&
        orderProduct.innerPackagingQuantity !== product.innerPackagingQuantity
      ) {
        updates.innerPackagingQuantity = orderProduct.innerPackagingQuantity;
        hasUpdates = true;
      }

      // Check if master packaging quantity differs
      if (
        orderProduct.masterPackagingQuantity !== undefined &&
        orderProduct.masterPackagingQuantity !== product.masterPackagingQuantity
      ) {
        updates.masterPackagingQuantity = orderProduct.masterPackagingQuantity;
        hasUpdates = true;
      }

      // Only update if there are changes
      if (hasUpdates) {
        updates.updatedAt = dayjs().unix();
        updates.updatedBy = email;

        await firestore.collection(FIRESTORE_PRODUCTS_TABLE).doc(orderProduct.productId).update(updates);
      }
    });

    await Promise.all(updatePromises);
  };

  const handleApproveChanges = async () => {
    try {
      setSaving(true);

      // Apply adjustedAmount, adjustedPrice, adjustedPackaging and adjustedDimensions to base values
      const updatedProducts = order.products.map((product) => {
        const updates = { ...product };

        if (product.adjustedAmount !== undefined && product.adjustedAmount !== product.amount) {
          updates.amount = product.adjustedAmount;
        }

        if (product.adjustedPrice !== undefined && product.adjustedPrice !== product.price) {
          updates.price = product.adjustedPrice;
        }

        if (
          product.adjustedInnerPackagingQuantity !== undefined &&
          product.adjustedInnerPackagingQuantity !== product.innerPackagingQuantity
        ) {
          updates.innerPackagingQuantity = product.adjustedInnerPackagingQuantity;
        }

        if (
          product.adjustedMasterPackagingQuantity !== undefined &&
          product.adjustedMasterPackagingQuantity !== product.masterPackagingQuantity
        ) {
          updates.masterPackagingQuantity = product.adjustedMasterPackagingQuantity;
        }

        if (
          product.adjustedProductDimensions &&
          JSON.stringify(product.adjustedProductDimensions) !== JSON.stringify(product.productDimensions)
        ) {
          updates.productDimensions = product.adjustedProductDimensions;
        }

        // Clear adjusted fields after approval
        delete updates.adjustedAmount;
        delete updates.adjustedPrice;
        delete updates.adjustedInnerPackagingQuantity;
        delete updates.adjustedMasterPackagingQuantity;
        delete updates.adjustedProductDimensions;

        return updates;
      });

      // Recalculate totalProducts and totalPrice based on updated products
      const totalProducts = updatedProducts.reduce((sum, product) => sum + product.amount, 0);
      const totalPrice = updatedProducts.reduce((sum, product) => {
        const price = product.price || 0;
        const amount = product.amount || 0;
        return sum + price * amount;
      }, 0);

      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "changes_approved",
        user: email,
        metadata: {
          message: "Supplier changes approved by Bombyxx",
          totalProducts,
          totalPrice,
        },
      });

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
        .doc(id)
        .update({
          products: updatedProducts,
          totalProducts,
          totalPrice,
          status: PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION,
          log: newLog,
          lastModifiedBy: { email, updatedAt: dayjs().unix() },
        });

      // Update product master data (prices and packaging) in products collection
      await updateProductPricesAndPackaging(updatedProducts);

      message.success(t("notifications.changes_approved"));
      await loadOrder();
    } catch (error) {
      console.error("Error approving changes:", error);
      message.error(t("notifications.changes_approval_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOrderStatus = async (newStatus) => {
    try {
      setSaving(true);
      const oldLog = order.log || [];
      const newLog = addLogEntry(oldLog, {
        action: "status_changed",
        user: email,
        oldValue: order.status,
        newValue: newStatus,
      });

      const updateData = {
        status: newStatus,
        log: newLog,
        lastModifiedBy: { email, updatedAt: dayjs().unix() },
      };

      if (newStatus === PURCHASE_ORDER_STATUS_RECEIVED) {
        updateData.receivedAt = dayjs().unix();
      }

      await firestore.collection(FIRESTORE_PURCHASE_ORDERS_TABLE).doc(id).update(updateData);

      message.success(t("notifications.purchase_order_status_update_successful"));
      await loadOrder();
    } catch (error) {
      console.error("Error updating order status:", error);
      message.error(t("notifications.purchase_order_status_update_failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteOrder = async () => {
    Modal.confirm({
      title: t("confirm_complete_order"),
      icon: <ExclamationCircleOutlined />,
      content: t("confirm_complete_order_description"),
      okText: t("yes"),
      cancelText: t("no"),
      onOk: async () => {
        try {
          setSaving(true);
          const oldLog = order.log || [];
          const newLog = addLogEntry(oldLog, {
            action: "status_changed",
            user: email,
            oldValue: order.status,
            newValue: PURCHASE_ORDER_STATUS_COMPLETED,
          });

          await firestore
            .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
            .doc(id)
            .update({
              status: PURCHASE_ORDER_STATUS_COMPLETED,
              completedAt: dayjs().unix(),
              log: newLog,
              lastModifiedBy: { email, updatedAt: dayjs().unix() },
            });

          message.success(t("notifications.purchase_order_completed"));
          await loadOrder();
        } catch (error) {
          console.error("Error completing order:", error);
          message.error(t("notifications.purchase_order_complete_failed"));
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // Data the supplier still owes us on this order. Drives both what the reminder
  // modal shows and what the mail asks for, so the preview and the mail can't diverge.
  const getMissingSupplierFields = () => {
    if (!order) return [];
    const missing = [];
    if (!order.cubicMeters) missing.push("CBM (cubic meters)");
    if (!order.etd) missing.push("ETD (estimated time of departure)");
    if (!order.priceConfirmedAt) missing.push("Price confirmation");
    return missing;
  };

  const handleSendSupplierReminder = async () => {
    const supplier = suppliers.find((s) => s.id === order.supplierId);
    if (!supplier?.email) {
      message.error("Deze leverancier heeft geen e-mailadres");
      return;
    }

    try {
      setSendingReminder(true);
      const missingFields = getMissingSupplierFields();
      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/email/purchase-orders/${id}/reminder`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({ ccEmail: email, missingFields, note: reminderNote.trim() || null }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const sentAt = dayjs().unix();
      // Written straight to the history sub-collection: the Cloud Function only
      // diffs a fixed set of order fields, so it would never pick this up itself.
      await firestore
        .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
        .doc(id)
        .collection("history")
        .add({
          changedBy: email,
          changedAt: sentAt,
          changes: [
            {
              field: "reminder_sent",
              metadata: missingFields.length
                ? `Herinnering verstuurd naar ${supplier.email} (ontbrekend: ${missingFields.join(", ")})`
                : `Herinnering verstuurd naar ${supplier.email}`,
            },
          ],
        });

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
        .doc(id)
        .update({ lastReminderSentAt: sentAt, lastReminderSentBy: email });

      message.success(`Herinnering verstuurd naar ${supplier.email} (kopie naar ${email})`);
      setShowReminderModal(false);
      setReminderNote("");
    } catch (error) {
      console.error("Error sending supplier reminder:", error);
      message.error("Fout bij versturen herinnering");
    } finally {
      setSendingReminder(false);
    }
  };

  /**
   * Mailt de openstaande regels van deze order naar de leverancier (BMX-12).
   *
   * De regels gaan mee in de request in plaats van dat de API ze zelf berekent: zo staat in de
   * mail exact wat de gebruiker in het overzicht zag voordat hij op versturen drukte. Zelfde
   * afweging als bij de herinnering (BMX-16); de CC komt aan serverzijde uit het geverifieerde
   * token, niet uit deze body.
   */
  const handleSendOpenItems = async () => {
    const supplier = suppliers.find((s) => s.id === order.supplierId);
    if (!supplier?.email) {
      message.error("Deze leverancier heeft geen e-mailadres");
      return;
    }

    const lines = buildOpenItems(order, shipments);
    if (!lines.length) {
      message.error("Er staat niets meer open op deze order");
      return;
    }

    try {
      setSendingOpenItems(true);
      const idToken = await getFirebaseIDToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/email/purchase-orders/${id}/open-items`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken },
        body: JSON.stringify({ ccEmail: email, note: openItemsNote.trim() || null, openItems: lines }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const sentAt = dayjs().unix();
      const totals = summarizeOpenItems(lines);
      // Rechtstreeks naar de history-subcollectie: de Cloud Function diff't alleen een vaste
      // set ordervelden en zou dit dus nooit zelf oppikken. Zelfde patroon als de herinnering.
      await firestore
        .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
        .doc(id)
        .collection("history")
        .add({
          changedBy: email,
          changedAt: sentAt,
          changes: [
            {
              field: "open_items_sent",
              metadata: `Openstaande items verstuurd naar ${supplier.email} (${totals.lines} regels, ${totals.quantity} stuks)`,
            },
          ],
        });

      await firestore
        .collection(FIRESTORE_PURCHASE_ORDERS_TABLE)
        .doc(id)
        .update({ lastOpenItemsSentAt: sentAt, lastOpenItemsSentBy: email });

      message.success(`Openstaande items verstuurd naar ${supplier.email} (kopie naar ${email})`);
      setShowOpenItemsModal(false);
      setOpenItemsNote("");
    } catch (error) {
      console.error("Error sending open items:", error);
      message.error("Fout bij versturen openstaande items");
    } finally {
      setSendingOpenItems(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const isReceived = order.status === PURCHASE_ORDER_STATUS_RECEIVED;
  const isShipped = order.status === PURCHASE_ORDER_STATUS_SHIPPED;
  const isUnderReview = order.status === PURCHASE_ORDER_STATUS_UNDER_REVIEW;
  const isPendingApproval = order.status === PURCHASE_ORDER_STATUS_PENDING_APPROVAL;
  const isReadyForProduction = order.status === PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION;
  const isInProduction = order.status === PURCHASE_ORDER_STATUS_IN_PRODUCTION;
  const hasReview = order.receivedProducts && order.receivedProducts.length > 0;
  const canComplete = isUnderReview && hasReview;
  // Statuses where the ball is in the supplier's court, so a reminder makes sense.
  const canSendReminder = [
    PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION,
    PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW,
    PURCHASE_ORDER_STATUS_IN_PRODUCTION,
  ].includes(order.status);
  const reminderSupplier = suppliers.find((s) => s.id === order.supplierId);
  const missingSupplierFields = getMissingSupplierFields();

  // Openstaande regels van deze order (BMX-12). Eén berekening voor het overzicht in de modal,
  // de Excel-export en de mail.
  const openItems = buildOpenItems(order, shipments);
  const openItemsSummary = summarizeOpenItems(openItems);
  // Een concept is nog niet bij de leverancier, en bij een geannuleerde order valt er niets meer
  // te produceren. Verder: alleen zinvol als er daadwerkelijk iets openstaat.
  const hasOpenItems =
    openItems.length > 0 &&
    ![PURCHASE_ORDER_STATUS_DRAFT, PURCHASE_ORDER_STATUS_CANCELLED].includes(order.status);

  // Exporteren gaat rechtstreeks, zonder tussenscherm: er valt niets te kiezen en niets te
  // versturen. De herinnering heeft die tussenstap wél nodig, want daar gaat post de deur uit.
  const handleExportOpenItems = () =>
    exportToExcel(getPurchaseOrderOpenItemsExcelExportData(openItems), `Openstaand ${order.orderNumber || id}`);

  // Producten + extra kosten apart en opgeteld; stond eerder als IIFE midden in
  // de Descriptions.
  // Zendingen van deze order: de koppeling staat op de zending
  // (`sourceOrders[].purchaseOrderId`), niet op de order.
  const linkedShipments = (shipments || [])
    .filter((s) => (s.sourceOrders || []).some((so) => so.purchaseOrderId === order.id))
    .map((s) => {
      // Zelfde matching als getShippedQuantityForOrder, zodat deze aantallen
      // overeenkomen met de kolom "Verzonden / open" in de productentabel.
      const lines = (s.items || []).filter((it) => it.purchaseOrderId === order.id || !it.purchaseOrderId);
      return {
        ...s,
        _lines: lines.length,
        _quantity: lines.reduce((sum, it) => sum + (it.quantity || 0), 0),
      };
    });

  // Regels, stuks en bedrag voor de kop-meta en de totaalrij van de
  // productentabel. Volgt dezelfde prijsafleiding als de kolommen zelf: een
  // productconcept heeft nog geen eigen prijs en leent die van het concept.
  const productTotals = (isEditing ? editedProducts : order.products || []).reduce(
    (acc, item) => {
      const price = !!item.price
        ? item.price
        : (item.productDraftId ? productDrafts[item.productDraftId]?.draftProduct?.purchasePrice : null) ?? 0;
      const quantity = item.adjustedAmount ?? item.amount ?? 0;
      return { lines: acc.lines + 1, quantity: acc.quantity + quantity, amount: acc.amount + price * quantity };
    },
    { lines: 0, quantity: 0, amount: 0 },
  );

  const shipmentCoverage = (order.products || []).reduce(
    (acc, product) => ({
      ordered: acc.ordered + (product.adjustedAmount ?? product.amount ?? 0),
      open: acc.open + Math.max(0, getRemainingQuantity(product) || 0),
    }),
    { ordered: 0, open: 0 },
  );

  const orderCostBreakdown = (() => {
    const additionalCosts = order.invoice?.additionalCosts?.reduce((sum, cost) => sum + cost.amount, 0) || 0;
    const products = order.totalPrice ?? 0;
    return { products, additionalCosts, total: products + additionalCosts };
  })();

  // Containervulling: hoeveel containers van 68 m³ deze order beslaat en wat er
  // in de laatste nog vrij is.
  const containerFill = (() => {
    const capacity = 68;
    const cbm = order.cubicMeters || 0;
    const containers = Math.max(1, Math.ceil(cbm / capacity));
    const totalCapacity = containers * capacity;
    return {
      containers,
      spaceLeft: Math.round((totalCapacity - cbm) * 100) / 100,
      percentage: totalCapacity ? Math.min(100, (cbm / totalCapacity) * 100) : 0,
    };
  })();

  // Detailvelden per orderregel: barcode, afmetingen, verpakking, opmerking,
  // afbeeldingen en het ontvangen aantal. Stonden in een tweede, complete
  // productenweergave onder de tabel ("Scroll naar beneden voor details"), in
  // twee tabbladen die exact dezelfde velden toonden — één voor volledig
  // verzonden regels, één voor de rest. Die splitsing is vervangen door de kolom
  // "Verzonden / Open", en deze velden zijn nu de uitklapbare rij van dezelfde
  // tabel. Het item "Besteld" is eruit: aantal, voortgang en annuleren staan in
  // de tabelrij zelf.
  const renderProductDetails = (item, index) => {
    const receivedItem = order.receivedProducts?.find(
      (rp) => rp.productId === item.productId,
    );
    const hasDiscrepancy =
      receivedItem &&
      receivedItem.receivedAmount !== (item.adjustedAmount || item.amount);
    const shippedQty = getShippedQuantity(item.productId);
    const canRemoveEntirely = shippedQty === 0;

    return (
      <Card
        key={index}
        size="small"
        extra={
          isEditing && (
            <Tooltip
              title={!canRemoveEntirely ? t("cannot_remove_shipped_product") : ""}
            >
              <Button
                type="text"
                danger
                disabled={!canRemoveEntirely}
                icon={<DeleteOutlined />}
                onClick={() => handleRemoveProduct(index)}
              >
                {t("delete")}
              </Button>
            </Tooltip>
          )
        }
      >
        <Descriptions
          title={
            <span>
              <strong>{item.productName}</strong>
              {item.productColor && (
                <span style={{ color: "#666", marginLeft: 8 }}>
                  ({item.productColor})
                </span>
              )}
              {item.productDraftId && (
                <Tag color="gold" style={{ marginLeft: 8 }}>
                  {t("product_draft.new_product")}
                </Tag>
              )}
            </span>
          }
          bordered
          column={1}
          size="small"
          labelStyle={{ maxWidth: 200, width: 200, color: "#000" }}
        >

          <StyledDescriptionsItem label="Code">
            {item.productCode || "-"}
          </StyledDescriptionsItem>

          <StyledDescriptionsItem label="Barcode">
            {item.productBarcode || "-"}
          </StyledDescriptionsItem>

          {item.productDraftId && productDrafts[item.productDraftId] ? (
            (() => {
              const dp = productDrafts[item.productDraftId].draftProduct;
              const dimType = dp?.dimensionType;
              let dimensionsText = "-";
              if (dimType === "lwh" && dp?.length && dp?.width && dp?.height) {
                dimensionsText = `L: ${dp.length} × B: ${dp.width} × H: ${dp.height} cm`;
              } else if (dimType === "hd" && dp?.height && dp?.diameter) {
                dimensionsText = `H: ${dp.height} × Ø: ${dp.diameter} cm`;
              } else if (dimType === "lw" && dp?.length && dp?.width) {
                dimensionsText = `L: ${dp.length} × B: ${dp.width} cm`;
              }
              const dimTypeLabel =
                dimType === "lwh"
                  ? "L × B × H"
                  : dimType === "hd"
                    ? "H × Diameter"
                    : dimType === "lw"
                      ? "L × B"
                      : "-";
              return (
                <>
                  <StyledDescriptionsItem label="Dimensietype">
                    {dimTypeLabel}
                  </StyledDescriptionsItem>
                  <StyledDescriptionsItem label="Afmetingen">
                    {dimensionsText}
                  </StyledDescriptionsItem>
                  <StyledDescriptionsItem label={t("inner_packaging_qty")}>
                    {dp?.innerPackagingQuantity ?? "-"}
                  </StyledDescriptionsItem>
                  <StyledDescriptionsItem label={t("master_packaging_qty")}>
                    {dp?.masterPackagingQuantity ?? "-"}
                  </StyledDescriptionsItem>
                  <StyledDescriptionsItem label={t("price_per_unit")}>
                    {dp?.purchasePrice != null
                      ? formatPriceByCurrency(dp.purchasePrice, order.currency)
                      : "-"}
                  </StyledDescriptionsItem>
                </>
              );
            })()
          ) : (
            <>
              <StyledDescriptionsItem label="Afmetingen">
                {(() => {
                  const currentDim =
                    item.adjustedProductDimensions || item.productDimensions;
                  const originalDim = item.productDimensions;
                  const hasChanges =
                    item.adjustedProductDimensions &&
                    JSON.stringify(item.adjustedProductDimensions) !==
                      JSON.stringify(item.productDimensions);

                  let currentText = "-";
                  let originalText = "";

                  if (currentDim) {
                    if (currentDim.length && currentDim.width && currentDim.height) {
                      currentText = `L: ${currentDim.length} × B: ${currentDim.width} × H: ${currentDim.height} cm`;
                    } else if (currentDim.height && currentDim.diameter) {
                      currentText = `H: ${currentDim.height} × Ø: ${currentDim.diameter} cm`;
                    } else if (currentDim.length && currentDim.width) {
                      currentText = `L: ${currentDim.length} × B: ${currentDim.width} cm`;
                    }
                  }

                  if (hasChanges && originalDim) {
                    if (
                      originalDim.length &&
                      originalDim.width &&
                      originalDim.height
                    ) {
                      originalText = `L: ${originalDim.length} × B: ${originalDim.width} × H: ${originalDim.height} cm`;
                    } else if (originalDim.height && originalDim.diameter) {
                      originalText = `H: ${originalDim.height} × Ø: ${originalDim.diameter} cm`;
                    } else if (originalDim.length && originalDim.width) {
                      originalText = `L: ${originalDim.length} × B: ${originalDim.width} cm`;
                    }
                  }

                  return (
                    <span>
                      {currentText}
                      {hasChanges && originalText && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>
                          → {currentText} (wijziging door leverancier, was:{" "}
                          {originalText})
                        </Tag>
                      )}
                    </span>
                  );
                })()}
              </StyledDescriptionsItem>

              <StyledDescriptionsItem label={t("inner_packaging_qty")}>
                <span>
                  {item.innerPackagingQuantity || "-"}
                  {item.adjustedInnerPackagingQuantity !== undefined &&
                    item.adjustedInnerPackagingQuantity !==
                      item.innerPackagingQuantity && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        → {item.adjustedInnerPackagingQuantity} (wijziging door
                        leverancier)
                      </Tag>
                    )}
                </span>
              </StyledDescriptionsItem>

              <StyledDescriptionsItem label={t("master_packaging_qty")}>
                <span>
                  {item.masterPackagingQuantity || "-"}
                  {item.adjustedMasterPackagingQuantity !== undefined &&
                    item.adjustedMasterPackagingQuantity !==
                      item.masterPackagingQuantity && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        → {item.adjustedMasterPackagingQuantity} (wijziging door
                        leverancier)
                      </Tag>
                    )}
                </span>
              </StyledDescriptionsItem>

              <StyledDescriptionsItem label={t("price_per_unit")}>
                {isEditing ? (
                  <Space>
                    <InputNumber
                      min={0}
                      step={0.01}
                      prefix={getCurrencyPrefix(order.currency) || "$"}
                      value={item.price}
                      onChange={(value) =>
                        handleProductChange(index, "price", value || 0)
                      }
                      style={{ width: 120 }}
                    />
                    {item.adjustedPrice !== undefined &&
                      item.adjustedPrice !== item.price && (
                        <Tag color="orange">
                          {t("was")}:{" "}
                          {formatPriceByCurrency(item.adjustedPrice, order.currency)}{" "}
                          ({t("supplier_proposal")})
                        </Tag>
                      )}
                  </Space>
                ) : (
                  <span>
                    {formatPriceByCurrency(item.price ?? 0, order.currency)}
                    {item.adjustedPrice !== undefined &&
                      item.adjustedPrice !== item.price && (
                        <Tag color="orange" style={{ marginLeft: 8 }}>
                          →{" "}
                          {formatPriceByCurrency(item.adjustedPrice, order.currency)}{" "}
                          (wijziging door leverancier)
                        </Tag>
                      )}
                  </span>
                )}
              </StyledDescriptionsItem>
            </>
          )}

          {item.comment && (
            <StyledDescriptionsItem label={<span>{t("comments")}</span>}>
              <div
                style={{
                  fontSize: 13,
                }}
              >
                {item.comment}
              </div>
            </StyledDescriptionsItem>
          )}

          {item.images && item.images.length > 0 && (
            <StyledDescriptionsItem label={<span>{t("images")}</span>}>
              <Space size="small" wrap>
                {item.images.map((img, idx) => (
                  <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                    <img
                      src={img}
                      alt={`Product image ${idx + 1}`}
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: "cover",
                        borderRadius: 4,
                        border: "1px solid #d9d9d9",
                      }}
                    />
                  </a>
                ))}
              </Space>
            </StyledDescriptionsItem>
          )}

          {receivedItem && (
            <StyledDescriptionsItem label={t("received")}>
              {receivedItem.receivedAmount}
              {hasDiscrepancy && (
                <Tag color="orange" style={{ marginLeft: 8 }}>
                  {t("discrepancy")}
                </Tag>
              )}
            </StyledDescriptionsItem>
          )}

          {receivedItem?.discrepancyReason && (
            <StyledDescriptionsItem label={t("discrepancy_reason")}>
              <div
                style={{
                  padding: 8,
                  background: "#fff7e6",
                  borderRadius: 4,
                  fontSize: 13,
                  border: "1px solid #ffd591",
                }}
              >
                {receivedItem.discrepancyReason}
              </div>
            </StyledDescriptionsItem>
          )}
        </Descriptions>
      </Card>
    );
  };

  return (
    <div className="purchase-detail-page">
      <PageHeader
        title={order.orderNumber}
        meta={
          <>
            <span>{order.supplierName}</span>
            {order.orderDate && (
              <>
                <span style={{ color: "rgba(0, 0, 0, 0.15)" }}>·</span>
                <Num>{dayjs.unix(order.orderDate).format("DD-MM-YYYY HH:mm")}</Num>
              </>
            )}
            {order.createdBy && (
              <>
                <span style={{ color: "rgba(0, 0, 0, 0.15)" }}>·</span>
                <span>door {order.createdBy}</span>
              </>
            )}
          </>
        }
        extra={
          <>
            <Select
              value={order.status}
              style={{ minWidth: 200 }}
              onChange={(newStatus) => {
                Modal.confirm({
                  title: t("confirm_status_change"),
                  icon: <ExclamationCircleOutlined />,
                  content: `${t(`order.${order.status.toLowerCase()}`)} → ${t(`order.${newStatus.toLowerCase()}`)}`,
                  okText: t("yes"),
                  cancelText: t("no"),
                  onOk: () => handleUpdateOrderStatus(newStatus),
                });
              }}
              loading={saving}
              options={[
                { value: PURCHASE_ORDER_STATUS_DRAFT, label: t("order.draft") },
                { value: PURCHASE_ORDER_STATUS_PENDING_CONFIRMATION, label: t("order.pending_confirmation") },
                { value: PURCHASE_ORDER_STATUS_PENDING_APPROVAL, label: t("order.pending_approval") },
                { value: PURCHASE_ORDER_STATUS_PENDING_SUPPLIER_REVIEW, label: t("order.pending_supplier_review") },
                { value: PURCHASE_ORDER_STATUS_READY_FOR_PRODUCTION, label: t("order.ready_for_production") },
                { value: PURCHASE_ORDER_STATUS_IN_PRODUCTION, label: t("order.in_production") },
                { value: PURCHASE_ORDER_STATUS_PARTIALLY_SHIPPED, label: t("order.partially_shipped") },
                { value: PURCHASE_ORDER_STATUS_SHIPPED, label: t("order.shipped") },
                { value: PURCHASE_ORDER_STATUS_COMPLETED, label: t("order.completed") },
                { value: PURCHASE_ORDER_STATUS_CANCELLED, label: t("order.cancelled") },
              ]}
            />

            {/* De actie die nú aan de beurt is, staat als enige primaire knop naast
                de status. Voorheen konden er vier primaire knoppen tegelijk staan. */}
            {order.status === PURCHASE_ORDER_STATUS_DRAFT && !isEditing && (
              // Een concept is nog niet af; verder samenstellen doe je in de
              // aanmaakwizard, niet in de regelbewerking van deze pagina.
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`${urls.purchaseOrdersList}/${order.id}/edit`)}
              >
                {t("edit")}
              </Button>
            )}
            {isPendingApproval && !isEditing && (
              <Button type="primary" onClick={handleApproveChanges} loading={saving}>
                {t("approve_changes")}
              </Button>
            )}
            {isUnderReview && !hasReview && !isEditing && (
              <Button type="primary" onClick={handleOpenReviewModal} loading={saving}>
                {t("review_received_items")}
              </Button>
            )}
            {!isEditing && (canComplete || canSendReminder || hasOpenItems) && (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    canComplete ? { key: "complete", label: t("complete_order") } : null,
                    canSendReminder ? { key: "reminder", label: "Herinnering sturen" } : null,
                    hasOpenItems ? { key: "exportOpenItems", label: "Openstaande items exporteren" } : null,
                    hasOpenItems ? { key: "openItemsReminder", label: "Herinnering openstaande items" } : null,
                  ].filter(Boolean),
                  onClick: ({ key }) => {
                    if (key === "complete") handleCompleteOrder();
                    if (key === "reminder") setShowReminderModal(true);
                    if (key === "exportOpenItems") handleExportOpenItems();
                    if (key === "openItemsReminder") setShowOpenItemsModal(true);
                  },
                }}
              >
                <Button icon={<MoreOutlined />} loading={saving} />
              </Dropdown>
            )}
          </>
        }
      />

      <DetailLayout
        aside={
          <>
            <SectionCard title="Gegevens">
              <KeyValueList
                items={[
                  {
                    label: t("order.status"),
                    value: (
                      <Tag color={getPurchaseOrderStatusColor(order.status)}>
                        {t(`order.${order.status.toLowerCase()}`)}
                      </Tag>
                    ),
                  },
                  { label: t("supplier.supplier"), value: order.supplierName },
                  {
                    label: t("order.order_date"),
                    value: <Num>{order.orderDate ? dayjs.unix(order.orderDate).format("DD-MM-YYYY") : "-"}</Num>,
                  },
                  order.etd ? { label: t("etd"), value: <Num>{dayjs.unix(order.etd).format("DD-MM-YYYY")}</Num> } : null,
                  order.orderedAt
                    ? {
                        label: t("ordered_at"),
                        value: <Num>{dayjs.unix(order.orderedAt).format("DD-MM-YYYY HH:mm")}</Num>,
                      }
                    : null,
                  order.receivedAt
                    ? {
                        label: t("received_at"),
                        value: <Num>{dayjs.unix(order.receivedAt).format("DD-MM-YYYY HH:mm")}</Num>,
                      }
                    : null,
                  order.completedAt
                    ? {
                        label: t("completed_at"),
                        value: <Num>{dayjs.unix(order.completedAt).format("DD-MM-YYYY HH:mm")}</Num>,
                      }
                    : null,
                  { label: t("currency"), value: <Num>{order.currency || "USD"}</Num> },
                  order.currency !== "IDR"
                    ? {
                        label: t("exchange_rate_usd_eur"),
                        value: order.exchangeRate ? <Num>1 USD = {order.exchangeRate.toFixed(4)} EUR</Num> : null,
                      }
                    : null,
                  { label: t("total_products"), value: <Num>{order.totalProducts}</Num> },
                  orderCostBreakdown.additionalCosts > 0
                    ? {
                        label: "Producten",
                        value: <Num>{formatPriceByCurrency(orderCostBreakdown.products, order.currency)}</Num>,
                      }
                    : null,
                  orderCostBreakdown.additionalCosts > 0
                    ? {
                        label: "Extra kosten",
                        value: (
                          <Num style={{ color: "#96601A" }}>
                            {formatPriceByCurrency(orderCostBreakdown.additionalCosts, order.currency)}
                          </Num>
                        ),
                      }
                    : null,
                  {
                    label: t("total_price"),
                    value: (
                      <Num strong size={14}>
                        {formatPriceByCurrency(orderCostBreakdown.total, order.currency)}
                      </Num>
                    ),
                  },
                  order.currency !== "IDR" && order.totalPrice && order.exchangeRate
                    ? {
                        label: t("total_price_eur"),
                        value: <Num>€ {formatPrice(order.totalPrice * order.exchangeRate)}</Num>,
                      }
                    : null,
                  order.trackingCode
                    ? {
                        label: t("tracking_code"),
                        // Een track & trace is meestal een URL van ~110 tekens.
                        // Die uitschrijven in de smalle zijkolom gaf een verticale
                        // sliert; als het een URL is tonen we een korte link met de
                        // volledige URL als tooltip.
                        value: /^https?:\/\//i.test(order.trackingCode) ? (
                          <a
                            href={order.trackingCode}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={order.trackingCode}
                          >
                            Openen <ExportOutlined style={{ fontSize: 11 }} />
                          </a>
                        ) : (
                          <Num nowrap={false}>{order.trackingCode}</Num>
                        ),
                        wrap: !/^https?:\/\//i.test(order.trackingCode),
                      }
                    : null,
                  { label: t("order.ordered_by"), value: order.createdBy },
                ]}
              />
            </SectionCard>

            {/* Containervulling: het ontwerp maakt hier een eigen blok van met een
                balk, omdat "hoeveel past er nog bij" de reden is om een order aan
                te vullen voordat hij de deur uit gaat. */}
            {order.cubicMeters ? (
              <SectionCard title="Containervulling" meta={<Num>{order.cubicMeters} m³</Num>}>
                <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ height: 6, borderRadius: 3, background: "#F2F1EE", overflow: "hidden" }}>
                    <div
                      style={{
                        height: 6,
                        width: `${Math.max(containerFill.percentage, 0.6)}%`,
                        minWidth: 3,
                        background: "#C9BFAF",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
                    <span style={{ fontSize: 12.5, color: "rgba(0, 0, 0, 0.55)" }}>
                      {containerFill.containers} container{containerFill.containers > 1 ? "s" : ""} van 68 m³
                    </span>
                    <Num size={12.5} muted>
                      {containerFill.percentage.toFixed(1).replace(".", ",")}%
                    </Num>
                  </div>
                  <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "rgba(0, 0, 0, 0.55)" }}>
                    {containerFill.spaceLeft === 0 ? (
                      <>Volledig gevuld.</>
                    ) : (
                      <>
                        Nog <Num>{containerFill.spaceLeft.toFixed(2).replace(".", ",")} m³</Num> vrij. Aanvullen
                        verlaagt de vrachtkosten per stuk.
                      </>
                    )}
                  </span>
                </div>
              </SectionCard>
            ) : null}

            {/* Zendingen van deze order. Hing eerder aan `order.shipmentIds` —
                een veld dat alleen het supplier-portal schrijft (en bij een
                concept-zending bewust niet, zie BMX-19). In het CMS was het
                daardoor vaak leeg en rendeerde de kaart helemaal niet. De
                koppeling loopt in werkelijkheid andersom: de zending noemt zijn
                orders in `sourceOrders`. */}
            <SectionCard
              title={t("purchase_order_shipments")}
              meta={`${linkedShipments.length} gekoppeld`}
            >
              {linkedShipments.length === 0 ? (
                <div style={{ padding: "14px 20px", fontSize: 13, color: "rgba(0, 0, 0, 0.4)" }}>
                  Nog geen zendingen gekoppeld.
                </div>
              ) : (
                <div>
                  {linkedShipments.map((shipment) => {
                    const isCancelled = shipment.status === "cancelled";
                    return (
                      <div
                        key={shipment.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) auto",
                          gap: 10,
                          alignItems: "center",
                          padding: "10px 20px",
                          borderBottom: "1px solid rgba(5, 5, 5, 0.04)",
                          opacity: isCancelled ? 0.6 : 1,
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                          <RecordLink
                            to={`${urls.purchaseOrderShipmentDetail}${shipment.id}`}
                            style={{ textDecoration: isCancelled ? "line-through" : undefined }}
                          >
                            {shipment.shipmentNumber || `${shipment.id.substring(0, 8)}…`}
                          </RecordLink>
                          <span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)" }}>
                            {shipment._lines} regels · {shipment._quantity} stuks
                          </span>
                        </div>
                        <Tag color={shipmentStatusColors[shipment.status] || "default"} style={{ marginInlineEnd: 0 }}>
                          {shipmentStatusLabels[shipment.status] || shipment.status}
                        </Tag>
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ padding: "12px 20px", fontSize: 12.5, color: "rgba(0, 0, 0, 0.55)", lineHeight: 1.5 }}>
                {shipmentCoverage.open === 0
                  ? `Alle ${shipmentCoverage.ordered} stuks zijn in een zending opgenomen.`
                  : `Nog ${shipmentCoverage.open} van ${shipmentCoverage.ordered} stuks niet in een zending.`}
              </div>
            </SectionCard>

            {(proformaInvoice || order.parentOrderNumber || (order.isSplit && order.childOrders?.length > 0)) && (
              <SectionCard title="Koppelingen">
                <div>
                  {proformaInvoice && (
                    <div style={{ padding: "10px 20px", borderBottom: "1px solid rgba(5, 5, 5, 0.04)" }}>
                      <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)", marginBottom: 2 }}>Proforma factuur</div>
                      <Space size={8} wrap>
                        <RecordLink to={`${urls.purchaseOrderInvoiceDetail}${proformaInvoice.id}`}>
                          {proformaInvoice.invoiceNumber}
                        </RecordLink>
                        {proformaInvoice.linkedFinalInvoiceId ? (
                          <Tag color="green" style={{ marginInlineEnd: 0 }}>
                            Verrekend
                          </Tag>
                        ) : (
                          <span style={{ fontSize: 12, color: "#96601A" }}>Nog niet verrekend</span>
                        )}
                      </Space>
                    </div>
                  )}
                  {order.parentOrderNumber && (
                    <div style={{ padding: "10px 20px", borderBottom: "1px solid rgba(5, 5, 5, 0.04)" }}>
                      <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)", marginBottom: 2 }}>
                        {t("parent_order")}
                      </div>
                      <RecordLink to={`${urls.purchaseOrdersList}/${order.parentOrderId}`}>
                        {order.parentOrderNumber}
                      </RecordLink>
                    </div>
                  )}
                  {order.isSplit && order.childOrders?.length > 0 && (
                    <div style={{ padding: "10px 20px" }}>
                      <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.4)", marginBottom: 4 }}>
                        {t("child_orders")}
                      </div>
                      <Space wrap size={8}>
                        {order.childOrders.map((childId, index) => (
                          <RecordLink key={childId} to={`${urls.purchaseOrdersList}/${childId}`}>
                            {order.orderNumber}-{String.fromCharCode(65 + index)}
                          </RecordLink>
                        ))}
                      </Space>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}
          </>
        }
      >
        <Card>
        {isPendingApproval && (
          <div
            style={{
              padding: "16px",
              marginBottom: "16px",
              background: "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: "4px",
            }}
          >
            <p style={{ margin: 0, marginBottom: 8, fontWeight: "bold" }}>⚠️ {t("supplier_made_changes")}</p>
            <p style={{ margin: 0, fontSize: "14px" }}>{t("check_changes_approve")}</p>
          </div>
        )}
        {order.comments && (
          <div style={{ marginTop: 24 }}>
            <h3>{t("comments")}</h3>
            <Card size="small" style={{ background: "#fafafa" }}>
              {order.comments}
            </Card>
          </div>
        )}

        {order.images && order.images.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3>{t("images")}</h3>
            <Space size="middle" wrap>
              {order.images.map((img, idx) => (
                <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                  <img
                    src={img}
                    alt={`Order image ${idx + 1}`}
                    style={{
                      width: 120,
                      height: 120,
                      objectFit: "cover",
                      borderRadius: 4,
                      border: "1px solid #d9d9d9",
                    }}
                  />
                </a>
              ))}
            </Space>
          </div>
        )}

        {/* Eén productenoverzicht in een eigen kaart, met kop-meta en een
            totaalrij — zoals in het ontwerp. De kop zei eerder
            "Producten — overzicht · Scroll naar beneden voor details", omdat er
            onder de tabel nog een tweede, uitgebreidere weergave stond. De CBM-tag
            die hier ook stond is weggehaald: die informatie staat sinds de
            herindeling volledig in de kaart Containervulling in de zijkolom. */}
        {/* Op mobiel is de tabel te breed; daar tonen de kaarten hieronder
            dezelfde regels. Eerder stonden tabel én kaarten naast elkaar. */}
        {!(isMobile && !isEditing) && (
        <SectionCard
          title={t("products")}
          meta={`${productTotals.lines} regels · ${productTotals.quantity} stuks`}
          extra={
            !isEditing ? (
              <Button size="small" icon={<EditOutlined />} onClick={handleStartEditing}>
                {t("edit")}
              </Button>
            ) : null
          }
          footer={
            isEditing ? (
              <Space direction="vertical" style={{ width: "100%" }} size={6}>
                <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                  <PlusOutlined /> {t("add_product")}
                </span>
                <Select
                  showSearch
                  style={{ width: "100%" }}
                  placeholder={t("search_select_product")}
                  optionFilterProp="children"
                  onChange={handleAddProduct}
                  value={null}
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  options={products
                    ?.filter((p) => !editedProducts.some((ep) => ep.productId === p.id))
                    .map((p) => ({
                      value: p.id,
                      label: `${p.name}${p.color ? ` (${p.color})` : ""} - ${p.code || ""}`,
                    }))}
                />
              </Space>
            ) : null
          }
        >
        <Table
          size="small"
          pagination={false}
          rowKey={(_, i) => i}
          dataSource={isEditing ? editedProducts : order.products}
          // Zonder scroll.x zet rc-table geen overflow op de tabel en loopt hij bij een
          // smalle hoofdkolom onder de zijkolom door. Een GETAL (geen "max-content"):
          // dat geeft width:Npx; min-width:100%, dus op een breed scherm vult de tabel
          // de kolom en breekt tekst gewoon af.
          scroll={{ x: isEditing ? 880 : 760 }}
          // Details per regel op aanvraag, in plaats van een tweede complete
          // productenweergave onder de tabel. Alleen uitklapbaar als er ook
          // daadwerkelijk iets extra's te zien is.
          expandable={{
            expandedRowRender: (item, index) => renderProductDetails(item, index),
            rowExpandable: (item) =>
              Boolean(
                item.productCode ||
                  item.productBarcode ||
                  item.productDimensions ||
                  item.adjustedProductDimensions ||
                  item.innerPackagingQuantity ||
                  item.masterPackagingQuantity ||
                  item.comment ||
                  item.images?.length ||
                  item.productDraftId ||
                  order.receivedProducts?.some((rp) => rp.productId === item.productId),
              ),
          }}
          // Totaalrij zoals in het ontwerp. Het totaal aantal en bedrag stonden
          // alleen in de zijkolom; onder de regels waar je ze optelt is logischer.
          summary={(rows) => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: "#FAFAF9" }}>
                <Table.Summary.Cell index={0}>{t("total")}</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="center">
                  <Num strong>{productTotals.quantity}</Num>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} align="right">
                  <Num strong size={14}>
                    {formatPriceByCurrency(productTotals.amount, order.currency)}
                  </Num>
                </Table.Summary.Cell>
                {isEditing ? <Table.Summary.Cell index={5} /> : null}
              </Table.Summary.Row>
            </Table.Summary>
          )}
          columns={[
            {
              title: "Product",
              key: "product",
              render: (_, item) => (
                <div>
                  <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {item.productName}
                    {item.productColor && (
                      <span style={{ color: "#888", fontWeight: 400, marginLeft: 4 }}>({item.productColor})</span>
                    )}
                    {item.priority && (
                      <Tag color="red" size="small" style={{ marginLeft: 6 }}>
                        🔥
                      </Tag>
                    )}
                    {/* Uitlopend komt van het product zelf, niet van de orderregel (BMX-49):
                        de vlag kan gezet zijn nadat deze order al was aangemaakt. */}
                    {isProductDiscontinued(item.productId) && (
                      <span style={{ marginLeft: 6 }}>
                        <DiscontinuedMark discontinued />
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#999" }}>{item.productCode || "-"}</div>
                </div>
              ),
            },
            {
              title: "Besteld",
              key: "ordered",
              width: isEditing ? 110 : 80,
              align: "center",
              render: (_, item, index) => {
                if (isEditing) {
                  const shippedQty = getShippedQuantity(item.productId);
                  return (
                    <InputNumber
                      min={shippedQty}
                      value={item.amount}
                      onChange={(value) => handleProductChange(index, "amount", value || 0)}
                      style={{ width: 90 }}
                    />
                  );
                }
                return (
                  <span style={{ fontWeight: 600 }}>
                    {item.adjustedAmount ?? item.amount}
                    {item.adjustedAmount !== undefined && item.adjustedAmount !== item.amount && (
                      <Tooltip title={`Origineel: ${item.amount}`}>
                        <Tag color="orange" size="small" style={{ marginLeft: 4, fontSize: 10 }}>
                          ✎
                        </Tag>
                      </Tooltip>
                    )}
                  </span>
                );
              },
            },
            {
              title: "Verzonden / Open",
              key: "shipment",
              width: 200,
              render: (_, item) => {
                const shippedQty = getShippedQuantity(item.productId);
                const orderedQty = item.adjustedAmount ?? item.amount;
                const cancelledAmount = item.cancelledAmount || 0;
                const remaining = getRemainingQuantity(item);
                const percent = orderedQty > 0 ? Math.min(100, Math.round((shippedQty / orderedQty) * 100)) : 0;
                const isComplete = shippedQty >= orderedQty;
                return (
                  <div style={{ minWidth: 140 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                      <span style={{ color: shippedQty > 0 ? "#1890ff" : "#bfbfbf" }}>✓ {shippedQty} verzonden</span>
                      {isComplete ? (
                        <span style={{ color: "#52c41a", fontWeight: 600 }}>Volledig</span>
                      ) : (
                        <span style={{ color: remaining > 0 ? "#fa8c16" : "#bfbfbf", fontWeight: 600 }}>
                          {remaining} open
                        </span>
                      )}
                    </div>
                    <Progress
                      percent={percent}
                      size="small"
                      strokeColor={isComplete ? "#52c41a" : "#1890ff"}
                      trailColor="#f0f0f0"
                      showInfo={false}
                    />
                    {cancelledAmount > 0 && (
                      <Tag color="default" style={{ marginTop: 4, fontSize: 10 }}>
                        {t("cancelled_amount_tag", { amount: cancelledAmount })}
                      </Tag>
                    )}
                  </div>
                );
              },
            },
            {
              title: "Prijs/stuk",
              key: "price",
              width: isEditing ? 150 : 100,
              align: "right",
              render: (_, item, index) => {
                const effectivePrice = !!item.price
                  ? item.price
                  : (item.productDraftId ? productDrafts[item.productDraftId]?.draftProduct?.purchasePrice : null) ?? 0;
                // De prijs was alleen bewerkbaar in het uitgebreide blok onder de
                // tabel; nu hier, net als het aantal. handleProductChange zet bij
                // veld "price" zelf ook adjustedPrice mee — dat blijft zo.
                if (isEditing) {
                  return (
                    <Space size={4}>
                      <InputNumber
                        min={0}
                        step={0.01}
                        value={item.price}
                        onChange={(value) => handleProductChange(index, "price", value || 0)}
                        style={{ width: 105 }}
                      />
                      {item.adjustedPrice !== undefined && item.adjustedPrice !== item.price && (
                        <Tooltip
                          title={`${t("supplier_proposal")}: ${formatPriceByCurrency(item.adjustedPrice, order.currency)}`}
                        >
                          <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                            ✎
                          </Tag>
                        </Tooltip>
                      )}
                    </Space>
                  );
                }
                return <Num muted>{formatPriceByCurrency(effectivePrice, order.currency)}</Num>;
              },
            },
            {
              title: "Subtotaal",
              key: "subtotal",
              width: 110,
              align: "right",
              render: (_, item) => {
                const effectivePrice = !!item.price
                  ? item.price
                  : (item.productDraftId ? productDrafts[item.productDraftId]?.draftProduct?.purchasePrice : null) ?? 0;
                return (
                  <Num>{formatPriceByCurrency(effectivePrice * (item.adjustedAmount ?? item.amount), order.currency)}</Num>
                );
              },
            },
            ...(isEditing
              ? [
                  {
                    title: t("actions"),
                    key: "actions",
                    width: 110,
                    align: "center",
                    render: (_, item, index) => {
                      const shippedQty = getShippedQuantity(item.productId);
                      const remaining = getRemainingQuantity(item);
                      const canRemoveEntirely = shippedQty === 0;
                      const canCancelRemaining = shippedQty > 0 && remaining > 0;
                      return (
                        <Space size={4}>
                          <Tooltip title={canCancelRemaining ? t("cancel_remaining_button") : ""}>
                            <Button
                              size="small"
                              disabled={!canCancelRemaining}
                              onClick={() => handleCancelRemaining(index, remaining)}
                              icon={<ExclamationCircleOutlined />}
                            />
                          </Tooltip>
                          <Tooltip title={!canRemoveEntirely ? t("cannot_remove_shipped_product") : t("delete")}>
                            <Button
                              size="small"
                              danger
                              disabled={!canRemoveEntirely}
                              onClick={() => handleRemoveProduct(index)}
                              icon={<DeleteOutlined />}
                            />
                          </Tooltip>
                        </Space>
                      );
                    },
                  },
                ]
              : []),
          ]}
        />
        </SectionCard>
        )}


        {isMobile && !isEditing && (
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            {order.products.map((item, index) => {
              const receivedItem = order.receivedProducts?.find((rp) => rp.productId === item.productId);
              const hasDiscrepancy =
                receivedItem && receivedItem.receivedAmount !== (item.adjustedAmount || item.amount);

              return (
                <MobileProductCard key={index}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: 0, marginBottom: 4, fontSize: 16, fontWeight: 600 }}>
                        {item.productName}
                        {item.productColor && (
                          <span style={{ color: "#666", marginLeft: 4 }}>({item.productColor})</span>
                        )}
                      </h4>
                      <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>{item.productCode || "-"}</div>
                      {item.productDraftId && (
                        <Tag color="gold" size="small">
                          {t("product_draft.new_product")}
                        </Tag>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#1890ff" }}>
                        {formatPriceByCurrency(item.price ?? 0, order.currency)}
                      </div>
                      {item.priority && (
                        <Tag color="red" size="small">
                          🔥 {t("priority")}
                        </Tag>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12 }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("ordered")}</div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>
                        {item.amount}
                        {item.adjustedAmount !== undefined && item.adjustedAmount !== item.amount && (
                          <Tag color="orange" size="small" style={{ marginLeft: 4 }}>
                            → {item.adjustedAmount}
                          </Tag>
                        )}
                      </div>
                      {(() => {
                        const shippedQty = getShippedQuantity(item.productId);
                        const orderedQty = item.adjustedAmount || item.amount;
                        const remaining = Math.max(0, orderedQty - shippedQty);
                        const percent = orderedQty > 0 ? Math.min(100, Math.round((shippedQty / orderedQty) * 100)) : 0;
                        const isComplete = shippedQty >= orderedQty;
                        const hasShipments = shippedQty > 0;
                        return (
                          <div>
                            <Progress
                              percent={percent}
                              size="small"
                              strokeColor={isComplete ? "#52c41a" : "#1890ff"}
                              trailColor="#f0f0f0"
                              format={() => null}
                              style={{ marginBottom: 2 }}
                            />
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                              <span style={{ color: hasShipments ? "#1890ff" : "#bfbfbf" }}>
                                ✓ {shippedQty} verzonden
                              </span>
                              {isComplete ? (
                                <span style={{ color: "#52c41a", fontWeight: 600 }}>Volledig</span>
                              ) : (
                                <span style={{ color: remaining > 0 ? "#fa8c16" : "#bfbfbf", fontWeight: 600 }}>
                                  {remaining} open
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("inner_packaging_qty")}</div>
                      <div style={{ fontWeight: 600 }}>{item.innerPackagingQuantity || "-"}</div>
                    </div>
                    <div>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("master_packaging_qty")}</div>
                      <div style={{ fontWeight: 600 }}>{item.masterPackagingQuantity || "-"}</div>
                    </div>
                    <div>
                      <div style={{ color: "#888", marginBottom: 2 }}>{t("subtotal")}</div>
                      <div style={{ fontWeight: 600, color: "#1890ff" }}>
                        {formatPriceByCurrency(
                          (item.price ?? 0) * (item.adjustedAmount ?? item.amount),
                          order.currency,
                        )}
                      </div>
                    </div>
                  </div>
                  {hasDiscrepancy && (
                    <div
                      style={{ marginTop: 8, padding: 8, backgroundColor: "#fff7e6", borderRadius: 4, fontSize: 12 }}
                    >
                      <strong style={{ color: "#fa8c16" }}>⚠️ {t("discrepancy")}</strong>
                      <div style={{ marginTop: 4 }}>
                        {t("received")}: {receivedItem.receivedAmount} (verwacht: {item.adjustedAmount || item.amount})
                      </div>
                    </div>
                  )}
                  {receivedItem?.notes && (
                    <div
                      style={{ marginTop: 8, padding: 8, backgroundColor: "#f6ffed", borderRadius: 4, fontSize: 12 }}
                    >
                      <strong>{t("notes")}:</strong> {receivedItem.notes}
                    </div>
                  )}
                </MobileProductCard>
              );
            })}
          </Space>
        )}

        {proformaInvoice && (
          <div style={{ marginTop: 24 }}>
            <h5 style={{ marginTop: 24 }}>Proforma factuur</h5>
            <Card size="small" style={{ backgroundColor: "#fffbe6" }}>
              <Space>
                <span style={{ fontWeight: 500 }}>{proformaInvoice.invoiceNumber || "—"}</span>
                {proformaInvoice.linkedFinalInvoiceId ? (
                  <Tag color="green">✓ Gekoppeld aan definitieve factuur</Tag>
                ) : (
                  <Tag>Nog niet gekoppeld</Tag>
                )}
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => navigate(`${urls.purchaseOrderInvoiceDetail}${proformaInvoice.id}`)}
                >
                  Bekijk proforma factuur
                </Button>
                {proformaInvoice.pdfUrl && (
                  <Button
                    type="link"
                    icon={<DownloadOutlined />}
                    href={proformaInvoice.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: 0 }}
                  >
                    Download PDF
                  </Button>
                )}
              </Space>
            </Card>
          </div>
        )}

        {/*{order.log && order.log.length > 0 && (*/}
        {/*  <div style={{ marginTop: 24 }}>*/}
        {/*    <Collapse defaultActiveKeys={["1"]}>*/}
        {/*      <Panel header={t("activity_log")} key="1">*/}
        {/*        <div style={{ maxHeight: 400, overflowY: "auto", paddingTop: 20 }}>*/}
        {/*          <Timeline>*/}
        {/*            {[...order.log].reverse().map((entry, idx) => (*/}
        {/*              <Timeline.Item key={idx}>*/}
        {/*                <div>*/}
        {/*                  <p style={{ margin: 0 }}>*/}
        {/*                    <strong>{t(`log.${entry.action}`)}</strong>*/}
        {/*                  </p>*/}
        {/*                  <p style={{ margin: 0, fontSize: 12, color: "#888" }}>*/}
        {/*                    {entry.user} - {dayjs.unix(entry.timestamp).format("DD-MM-YYYY HH:mm")}*/}
        {/*                  </p>*/}
        {/*                  {entry.oldValue && entry.newValue && (*/}
        {/*                    <p style={{ margin: 0, fontSize: 12 }}>*/}
        {/*                      {t(`order.${entry.oldValue}`)} → {t(`order.${entry.newValue}`)}*/}
        {/*                    </p>*/}
        {/*                  )}*/}
        {/*                  {entry.metadata && (*/}
        {/*                    <p style={{ margin: 0, fontSize: 12, color: "#666" }}>{JSON.stringify(entry.metadata)}</p>*/}
        {/*                  )}*/}
        {/*                </div>*/}
        {/*              </Timeline.Item>*/}
        {/*            ))}*/}
        {/*          </Timeline>*/}
        {/*        </div>*/}
        {/*      </Panel>*/}
        {/*    </Collapse>*/}
        {/*  </div>*/}
        {/*)}*/}
        </Card>

        {/* Geschiedenis onderaan de hoofdkolom: het is naslag, geen werk. Stond
            eerder bovenaan, boven de gegevens en de producten. */}
        {/* Geschiedenis (Cloud Function history sub-collectie) */}
        <div style={{ marginBottom: 24 }}>
          <Collapse>
            <Panel
              header={
                <span>
                  Geschiedenis{" "}
                  {!historyLoading && orderHistory.length > 0 && (
                    <Tag style={{ marginLeft: 4 }}>{orderHistory.length}</Tag>
                  )}
                </span>
              }
              key="history"
            >
              {historyLoading ? (
                <div style={{ textAlign: "center", padding: 24 }}>
                  <Spin />
                </div>
              ) : orderHistory.length === 0 ? (
                <div style={{ color: "#999", padding: 8 }}>Nog geen geschiedenis beschikbaar.</div>
              ) : (
                <div style={{ maxHeight: 500, overflowY: "auto", paddingTop: 8 }}>
                  <Timeline>
                    {orderHistory.map((entry) => (
                      <Timeline.Item key={entry.id}>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ fontWeight: 500 }}>{entry.changedBy}</span>
                          <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>
                            {dayjs.unix(entry.changedAt).format("DD-MM-YYYY HH:mm")}
                          </span>
                        </div>
                        {entry.changes?.map((change, idx) => {
                          if (change.field === "order_created") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#52c41a" }}>
                                ✓ Order aangemaakt
                              </div>
                            );
                          }
                          if (change.field === "price_confirmed") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#52c41a" }}>
                                ✓ Prijzen bevestigd door leverancier
                              </div>
                            );
                          }
                          if (change.field === "reminder_sent") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#1890ff" }}>
                                ✉ {change.metadata || "Herinnering verstuurd naar leverancier"}
                              </div>
                            );
                          }
                          if (change.field === "open_items_sent") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#1890ff" }}>
                                ✉ {change.metadata || "Openstaande items verstuurd naar leverancier"}
                              </div>
                            );
                          }
                          if (change.field === "status") {
                            return (
                              <div key={idx} style={{ fontSize: 13, marginBottom: 6 }}>
                                Status: <Tag>{t(`order.${change.oldValue}`)}</Tag>
                                <ArrowRightOutlined style={{ marginRight: 8 }} />
                                <Tag color="blue">{t(`order.${change.newValue}`)}</Tag>
                              </div>
                            );
                          }
                          if (change.field === "price_confirmed") {
                            return (
                              <div key={idx} style={{ fontSize: 13, marginBottom: 6 }}>
                                <Tag color={"green"}>{t(`order.${change.field}`)}</Tag>
                              </div>
                            );
                          }
                          if (change.field === "product_amount") {
                            return (
                              <div key={idx} style={{ fontSize: 13 }}>
                                <span style={{ color: "#666" }}>
                                  {change.productName} ({change.productCode}):
                                </span>{" "}
                                Aantal{" "}
                                <Tag color={change.newValue < change.oldValue ? "red" : "green"}>
                                  {change.oldValue}x <ArrowRightOutlined style={{ marginRight: 4 }} />
                                  {change.newValue}x
                                </Tag>
                              </div>
                            );
                          }
                          if (change.field === "product_price") {
                            return (
                              <div key={idx} style={{ fontSize: 13 }}>
                                <span style={{ color: "#666" }}>
                                  {change.productName} ({change.productCode}):
                                </span>{" "}
                                Prijs{" "}
                                <Tag>
                                  {change.oldValue} <ArrowRightOutlined style={{ marginRight: 8 }} /> {change.newValue}
                                </Tag>
                              </div>
                            );
                          }
                          if (change.field === "product_added") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#1890ff" }}>
                                + Product toegevoegd: {change.productName} ({change.productCode}) — {change.newValue}x
                              </div>
                            );
                          }
                          if (change.field === "product_removed") {
                            return (
                              <div key={idx} style={{ fontSize: 13, color: "#ff4d4f" }}>
                                − Product verwijderd: {change.productName} ({change.productCode}) — {change.oldValue}x
                              </div>
                            );
                          }
                          if (change.field === "product_cancelled") {
                            return (
                              <div key={idx} style={{ fontSize: 13 }}>
                                <span style={{ color: "#666" }}>
                                  {change.productName} ({change.productCode}):
                                </span>{" "}
                                Geannuleerd{" "}
                                <Tag color="orange">
                                  {change.oldValue}x <ArrowRightOutlined style={{ marginRight: 4 }} />
                                  {change.newValue}x
                                </Tag>
                              </div>
                            );
                          }
                          return (
                            <div key={idx} style={{ fontSize: 12, color: "#888" }}>
                              {change.field}: {JSON.stringify(change.oldValue)}{" "}
                              <ArrowRightOutlined style={{ marginRight: 8 }} /> {JSON.stringify(change.newValue)}
                            </div>
                          );
                        })}
                      </Timeline.Item>
                    ))}
                  </Timeline>
                </div>
              )}
            </Panel>
          </Collapse>
        </div>
      </DetailLayout>

      {/* Opslaan/annuleren voor de bewerkmodus. Deze balk zat in het
          productenblok dat is opgegaan in de tabel; zonder hem was er in
          bewerkmodus geen manier meer om op te slaan. Nu dezelfde
          StickyActionBar als op Zending detail, met de lijst wijzigingen erin. */}
      {isEditing &&
        (() => {
          const pendingChanges = getPendingChanges();
          // Als lopende zin in plaats van een rij tags: de balk staat er juist om
          // rust te geven, en een rij gekleurde tags maakt hem net zo druk als
          // waar je vandaan komt. De volledige lijst staat in de tooltip.
          return (
            <StickyActionBar
              note={
                pendingChanges.length === 0 ? (
                  t("no_pending_changes")
                ) : (
                  // Alleen de telling in de balk; de regels zelf achter een popover.
                  // Eerst stonden ze als tags (druk) en daarna als één doorlopende
                  // zin die afkapte — in beide gevallen kon je er niets mee. Zo blijft
                  // de balk rustig én is precies te zien wát er verandert.
                  <Popover
                    // Hover opent hem meteen; klik blijft werken voor touch en
                    // toetsenbord, en houdt hem open terwijl je in de lijst scrolt.
                    trigger={["hover", "click"]}
                    mouseEnterDelay={0.15}
                    placement="topLeft"
                    title={`Wijzigingen (${pendingChanges.length})`}
                    content={
                      <div style={{ minWidth: 280, maxWidth: 420, maxHeight: 320, overflowY: "auto" }}>
                        {pendingChanges.map((change, index) => (
                          <div
                            key={change.key}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 1fr) auto",
                              gap: 16,
                              alignItems: "baseline",
                              padding: "6px 0",
                              borderBottom:
                                index === pendingChanges.length - 1 ? undefined : "1px solid rgba(5, 5, 5, 0.04)",
                            }}
                          >
                            <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 13 }}>{change.label}</span>
                              {/* Welk veld er verandert. Zonder dit staat hetzelfde
                                  product twee keer in de lijst — één keer voor het
                                  aantal, één keer voor de prijs — zonder verschil. */}
                              <span style={{ fontSize: 11.5, color: "rgba(0, 0, 0, 0.45)" }}>
                                {CHANGE_TYPE_LABELS[change.type] || change.type}
                              </span>
                            </span>
                            <Num size={12.5}>{change.detail}</Num>
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <Button type="link" style={{ padding: 0, height: "auto", fontSize: 13 }}>
                      {t("pending_changes_count", { count: pendingChanges.length })}
                    </Button>
                  </Popover>
                )
              }
            >
              <Button onClick={handleCancelEditing}>{t("cancel")}</Button>
              <Button type="primary" onClick={handleSaveAndSendToSupplier} loading={saving}>
                {t("save_and_send_supplier")}
              </Button>
            </StickyActionBar>
          );
        })()}

      {/* Supplier reminder (BMX-16) */}
      <Modal
        title="Herinnering sturen naar leverancier"
        open={showReminderModal}
        onCancel={() => setShowReminderModal(false)}
        onOk={handleSendSupplierReminder}
        okText="Versturen"
        cancelText="Annuleren"
        confirmLoading={sendingReminder}
        okButtonProps={{ disabled: !reminderSupplier?.email }}
        width={isMobile ? "100%" : 600}
      >
        {!reminderSupplier?.email ? (
          <Alert
            type="error"
            showIcon
            message="Geen e-mailadres bekend"
            description="Deze leverancier heeft geen e-mailadres. Vul dat eerst aan bij de leverancier."
          />
        ) : (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Aan">{reminderSupplier.email}</Descriptions.Item>
              <Descriptions.Item label="CC">{email}</Descriptions.Item>
              <Descriptions.Item label="Order">{order.orderNumber}</Descriptions.Item>
            </Descriptions>

            {missingSupplierFields.length > 0 ? (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="De leverancier wordt gevraagd de order te bevestigen en dit aan te vullen:"
                description={
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {missingSupplierFields.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                }
              />
            ) : (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Alle gegevens zijn compleet - de leverancier wordt alleen gevraagd de order te bevestigen."
              />
            )}

            {order.lastReminderSentAt && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Laatste herinnering verstuurd op ${dayjs
                  .unix(order.lastReminderSentAt)
                  .format("DD-MM-YYYY HH:mm")}${order.lastReminderSentBy ? ` door ${order.lastReminderSentBy}` : ""}`}
              />
            )}

            <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Extra bericht (optioneel)</label>
            <TextArea
              rows={3}
              value={reminderNote}
              onChange={(e) => setReminderNote(e.target.value)}
              placeholder="Wordt onderaan de mail toegevoegd"
            />
          </>
        )}
      </Modal>

      {/* Herinnering met de openstaande items (BMX-12). Exporteren zit niet in deze modal maar
          als eigen actie in het menu: dat is een download, geen bericht aan de leverancier. */}
      <Modal
        title="Herinnering openstaande items"
        open={showOpenItemsModal}
        onCancel={() => setShowOpenItemsModal(false)}
        width={isMobile ? "100%" : 720}
        footer={[
          <Button key="cancel" onClick={() => setShowOpenItemsModal(false)}>
            Annuleren
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={sendingOpenItems}
            disabled={!reminderSupplier?.email || openItems.length === 0}
            onClick={handleSendOpenItems}
          >
            Herinnering sturen
          </Button>,
        ]}
      >
        <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Aan">
            {reminderSupplier?.email || <span style={{ color: "#ff4d4f" }}>Geen e-mailadres bekend</span>}
          </Descriptions.Item>
          <Descriptions.Item label="CC">{email}</Descriptions.Item>
          <Descriptions.Item label="Order">{order.orderNumber}</Descriptions.Item>
          <Descriptions.Item label="Nog open">
            {openItemsSummary.lines} regels · {openItemsSummary.quantity} stuks
          </Descriptions.Item>
        </Descriptions>

        {!reminderSupplier?.email && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Deze leverancier heeft geen e-mailadres"
            description="Exporteren kan wel; mailen pas nadat het e-mailadres bij de leverancier is ingevuld."
          />
        )}

        {order.lastOpenItemsSentAt && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Laatst verstuurd op ${dayjs
              .unix(order.lastOpenItemsSentAt)
              .format("DD-MM-YYYY HH:mm")}${order.lastOpenItemsSentBy ? ` door ${order.lastOpenItemsSentBy}` : ""}`}
          />
        )}

        {/* Exact de regels die ook in het bestand en de mail terechtkomen - één berekening. */}
        <Table
          size="small"
          pagination={false}
          scroll={{ x: 420 }}
          rowKey={(line, index) => line.productId || index}
          dataSource={openItems}
          columns={[
            {
              title: "Product",
              key: "product",
              render: (_, line) => (
                <div>
                  <div style={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {line.productName}
                    {line.productColor && (
                      <span style={{ color: "#888", fontWeight: 400, marginLeft: 4 }}>({line.productColor})</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#999" }}>{line.productCode || "-"}</div>
                </div>
              ),
            },
            { title: "Besteld", dataIndex: "ordered", key: "ordered", width: 80, align: "center" },
            { title: "Verzonden", dataIndex: "shipped", key: "shipped", width: 90, align: "center" },
            {
              title: "Open",
              dataIndex: "open",
              key: "open",
              width: 80,
              align: "center",
              render: (value) => <span style={{ fontWeight: 600, color: "#fa8c16" }}>{value}</span>,
            },
          ]}
        />

        <label style={{ display: "block", margin: "16px 0 8px", fontWeight: 500 }}>Extra bericht (optioneel)</label>
        <TextArea
          rows={3}
          value={openItemsNote}
          onChange={(e) => setOpenItemsNote(e.target.value)}
          placeholder="Wordt onderaan de mail toegevoegd"
        />
      </Modal>

      <Modal
        title={t("review_received_items")}
        open={showReviewModal}
        onCancel={() => setShowReviewModal(false)}
        onOk={handleSubmitReview}
        okText={t("submit_review")}
        cancelText={t("cancel")}
        confirmLoading={saving}
        width={isMobile ? "100%" : 800}
        styles={{ body: { padding: isMobile ? 12 : 24 } }}
      >
        <Form form={reviewForm} layout="vertical">
          <p style={{ marginBottom: 16, color: "#666" }}>{t("review_instructions")}</p>
          <Form.List name="products">
            {(fields) => (
              <>
                {fields.map((field) => {
                  const product = reviewForm.getFieldValue(["products", field.name]);
                  return (
                    <Card key={field.key} size="small" style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 8 }}>
                        <strong>{product?.productName}</strong>
                        {product?.productColor && <span style={{ color: "#666" }}> ({product.productColor})</span>}
                      </div>

                      <Space direction="vertical" style={{ width: "100%" }}>
                        <div>
                          <span style={{ color: "#666" }}>{t("expected_amount")}:</span>{" "}
                          <strong>{product?.expectedAmount}</strong>
                        </div>
                        <Form.Item
                          {...field}
                          name={[field.name, "receivedAmount"]}
                          label={t("actually_received")}
                          rules={[
                            { required: true, message: t("please_enter_received_amount") },
                            { type: "number", min: 0, message: t("must_be_positive") },
                          ]}
                        >
                          <InputNumber style={{ width: "100%" }} min={0} />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {() => {
                            const receivedAmount = reviewForm.getFieldValue(["products", field.name, "receivedAmount"]);
                            const expectedAmount = product?.expectedAmount;
                            const hasDiscrepancy = receivedAmount !== expectedAmount;

                            return hasDiscrepancy ? (
                              <Form.Item
                                {...field}
                                name={[field.name, "discrepancyReason"]}
                                label={t("discrepancy_reason")}
                                rules={[{ required: true, message: t("please_enter_reason") }]}
                              >
                                <TextArea rows={2} placeholder={t("explain_why_amounts_differ")} />
                              </Form.Item>
                            ) : null;
                          }}
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "productId"]} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "productName"]} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "productColor"]} hidden>
                          <Input />
                        </Form.Item>{" "}
                        <Form.Item {...field} name={[field.name, "productCode"]} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item {...field} name={[field.name, "expectedAmount"]} hidden>
                          <InputNumber />
                        </Form.Item>
                      </Space>
                    </Card>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

const MobileProductCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 4px;

  .ant-card-body {
    padding: 12px;
  }
`;

const StyledDescriptionsItem = styled(Descriptions.Item)`
  .ant-descriptions-item-label {
    font-weight: 500;
  }
`;

export default PurchaseOrderDetail;
